import { Head } from 'vite-react-ssg';
import { Link } from "react-router-dom";
import NavigationButtons from '@/components/NavigationButtons';
import { Chrome, Bot, Zap, Brain, Code, Scale, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { WaitlistDialog } from "@/components/WaitlistDialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const DeepseekR1 = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const faqItems = [
    {
      question: "What is DeepSeek R1?",
      answer: "DeepSeek R1 was DeepSeek's first open-source reasoning model, released in January 2025. It popularised visible chain-of-thought reasoning and rivalled OpenAI's o1 at a fraction of the cost. It is no longer DeepSeek's current model: reasoning now runs inside DeepSeek V4, and the deepseek-reasoner API alias that served R1-style output was retired on 24 July 2026."
    },
    {
      question: "Is DeepSeek R1 free to use?",
      answer: "The R1 weights are still available on Hugging Face under an MIT licence, so you can download and self-host them for free. What is gone is the hosted route: the deepseek-reasoner API alias was retired on 24 July 2026, so new API work should target deepseek-v4-flash or deepseek-v4-pro."
    },
    {
      question: "Should I still use DeepSeek R1 in 2026?",
      answer: "Only for research, reproducibility or offline self-hosting. For anything production-facing, DeepSeek V4 supersedes R1 on reasoning, coding and cost, and it is the only line DeepSeek still serves through its API."
    },
    {
      question: "Can I run DeepSeek R1 locally?",
      answer: "Yes. The R1 weights remain downloadable from Hugging Face and run under vLLM, SGLang or Ollama (distilled variants for smaller GPUs). Self-hosting is now the main reason to still pick R1 over V4."
    },
    {
      question: "What are DeepSeek R1's main capabilities?",
      answer: "R1 was built for step-by-step reasoning: mathematics, logical deduction, code generation and multi-step analysis, with its chain of thought exposed in the output. Those capabilities now live in DeepSeek V4, which reasons internally and scores higher on the same benchmarks."
    },
    {
      question: "Is DeepSeek R1 safe and secure?",
      answer: "R1 shipped with content filtering, and because the weights are open they can be audited and run entirely on your own infrastructure — which is the strongest privacy option available. Note that R1 no longer receives updates or safety patches from DeepSeek."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "DeepSeek R1",
    "applicationCategory": "Artificial Intelligence",
    "operatingSystem": "Cross-platform",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "DeepSeek R1 (January 2025) was DeepSeek's first open-source reasoning model. It has been superseded by DeepSeek V4; the hosted deepseek-reasoner alias was retired on 24 July 2026, while the MIT-licensed weights remain available for self-hosting.",
    "publisher": {
      "@type": "Organization",
      "name": "DeepSeek",
      "url": "https://www.deepseek.com"
    }
  };

  return (
    <>
      <Head>
        <title>DeepSeek R1 in 2026: Retired API, Weights Still Free</title>
        <meta 
          name="description" 
          content="DeepSeek R1 is superseded by V4 and its deepseek-reasoner API alias was retired 24 July 2026. What R1 was, why it mattered, and how to self-host the MIT weights." 
        />
        <meta name="keywords" content="DeepSeek R1, DeepSeek AI model, open source AI, reasoning AI, GPT-5 alternative, free AI model, AI coding, AI math" />
        <link rel="canonical" href="https://deepseek.ai/deepseek-r1" />
        
        <meta property="og:title" content="DeepSeek R1 in 2026: Retired API, Weights Still Free" />
        <meta property="og:description" content="DeepSeek R1 is history, not the current model: V4 replaced it and the deepseek-reasoner alias retired on 24 July 2026. The MIT weights are still free to self-host." />
        <meta property="og:url" content="https://deepseek.ai/deepseek-r1" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="DeepSeek R1 in 2026: Retired API, Weights Still Free" />
        <meta name="twitter:description" content="DeepSeek R1 is history, not the current model: V4 replaced it and the deepseek-reasoner alias retired on 24 July 2026. The MIT weights are still free to self-host." />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      </Head>

      <main className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">DeepSeek R1</h1>
          <p className="text-xl text-muted-foreground mb-6">
            The open-source reasoning model DeepSeek released in January 2025. R1 is now historical:
            DeepSeek V4 has replaced it, and the hosted <code>deepseek-reasoner</code> alias was retired
            on 24 July 2026. The MIT-licensed weights are still free to download and self-host.
          </p>

          <div className="rounded-xl border border-amber-300 bg-amber-50 text-left p-4 mb-6">
            <p className="text-sm text-amber-900">
              <strong>Superseded model.</strong> For current pricing, benchmarks and API names, see{" "}
              <Link to="/deepseek-v4" className="underline font-medium">DeepSeek V4</Link> and the{" "}
              <Link to="/blog/deepseek-chat-reasoner-retired-billing-impact" className="underline font-medium">
                deepseek-reasoner migration guide
              </Link>. This page is kept as a reference for R1 itself.
            </p>
          </div>
          
          <NavigationButtons />
        </div>

        {/* Key Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">What made R1 significant</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <Brain className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">Advanced Reasoning</h3>
              <p className="text-muted-foreground">
                Chain-of-thought reasoning for complex problem-solving and logical deduction.
              </p>
            </div>
            
            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <Code className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">Code Generation</h3>
              <p className="text-muted-foreground">
                Powerful coding capabilities across multiple programming languages.
              </p>
            </div>
            
            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <Zap className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">Math Excellence</h3>
              <p className="text-muted-foreground">
                State-of-the-art performance on mathematical reasoning benchmarks.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-card shadow-lg border">
              <Scale className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-3">Open Source</h3>
              <p className="text-muted-foreground">
                Fully open-source with MIT license. Run locally or use via API.
              </p>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-2">How R1 compared at launch (January 2025)</h2>
          <p className="text-center text-muted-foreground mb-8">
            A snapshot of the 2025 landscape, kept for context. It is not a comparison of today&rsquo;s models —
            for that, see <Link to="/deepseek-v4" className="text-primary hover:underline">DeepSeek V4</Link>.
          </p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Feature</TableHead>
                  <TableHead className="text-center">DeepSeek R1</TableHead>
                  <TableHead className="text-center">GPT-4</TableHead>
                  <TableHead className="text-center">Claude 3.5</TableHead>
                  <TableHead className="text-center">Gemini Pro</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Open Source</TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Free Tier</TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                  <TableCell className="text-center">Limited</TableCell>
                  <TableCell className="text-center">Limited</TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Local Deployment</TableCell>
                  <TableCell className="text-center"><CheckCircle className="h-5 w-5 text-green-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                  <TableCell className="text-center"><XCircle className="h-5 w-5 text-red-500 mx-auto" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Math Reasoning</TableCell>
                  <TableCell className="text-center">Excellent</TableCell>
                  <TableCell className="text-center">Excellent</TableCell>
                  <TableCell className="text-center">Very Good</TableCell>
                  <TableCell className="text-center">Good</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Code Generation</TableCell>
                  <TableCell className="text-center">Excellent</TableCell>
                  <TableCell className="text-center">Excellent</TableCell>
                  <TableCell className="text-center">Excellent</TableCell>
                  <TableCell className="text-center">Very Good</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">API Cost</TableCell>
                  <TableCell className="text-center">Very Low</TableCell>
                  <TableCell className="text-center">High</TableCell>
                  <TableCell className="text-center">Medium</TableCell>
                  <TableCell className="text-center">Medium</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <p className="text-center text-sm text-muted-foreground mt-4">
            See detailed comparisons: <Link to="/deepseek-vs-chatgpt" className="text-primary hover:underline">DeepSeek vs ChatGPT</Link> | <Link to="/deepseek-vs-claude" className="text-primary hover:underline">DeepSeek vs Claude</Link> | <Link to="/deepseek-vs-gemini" className="text-primary hover:underline">DeepSeek vs Gemini</Link>
          </p>
        </section>

        {/* FAQ Section */}
        <section className="mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">{item.question}</AccordionTrigger>
                <AccordionContent>{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Related Resources */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Learn More About DeepSeek</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/blog/what-is-deepseek-ai" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">What is DeepSeek AI?</h3>
              <p className="text-muted-foreground">Learn about DeepSeek's mission, technology, and impact on the AI industry.</p>
            </Link>
            <Link to="/deepseek-api" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek API</h3>
              <p className="text-muted-foreground">Current API models, pricing and the V4 endpoints that replaced deepseek-reasoner.</p>
            </Link>
            <Link to="/deepseek-coder" className="p-6 rounded-xl bg-card shadow-lg border hover:shadow-xl transition-shadow">
              <h3 className="text-xl font-semibold mb-2">DeepSeek Coder</h3>
              <p className="text-muted-foreground">Specialized coding model for software development tasks.</p>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-primary/5 rounded-2xl p-12">
          <h2 className="text-3xl font-bold mb-4">Moving on from R1</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            R1 still runs fine on your own hardware, but new work belongs on DeepSeek V4. Our free Chrome
            sidebar and the V4 guide are the quickest way to pick up where R1 left off.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/deepseek-api"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-primary-foreground bg-primary hover:bg-primary/90 transition-all shadow-lg"
            >
              Explore the V4 API
            </Link>
            <a
              href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-primary text-base font-medium rounded-full text-primary bg-background hover:bg-accent transition-all"
            >
              <Chrome className="mr-2 h-5 w-5" aria-hidden="true" />
              Add to Chrome - It's Free
            </a>
          </div>
        </section>

        <WaitlistDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
      </main>
    </>
  );
};

export default DeepseekR1;
